import { useState, useEffect, useMemo } from 'react';
import { Button, Table, Tag, Avatar, Modal, Tooltip } from 'antd';
import {
  LoadingOutlined,
  CrownOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  LogoutOutlined,
} from '@ant-design/icons';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import MultiEmailInput from '../MultiEmailInput/MultiEmailInput';
import axios from 'axios';
import { toast } from 'react-toastify';
import moment from 'moment';
import { BASE_URL } from '../../constants/api-urls';
import { SPOTIFY_CODE, SPOTIFY_REFRESH_TOKEN, SPOTIFY_TOKEN, SPOTIFY_USER_INFO } from '../../constants/storage';
import { usePackageContext } from '../../context/PackageContext';
import { TeamManagementTabStyle } from './TeamManagementTab.style';
import { getActiveWalletPlan, getStripePlanType } from '../../utils/planType';

interface Profile {
  _id: string;
  username: string;
  image: string;
}

interface Invitation {
  id: string;
  inviteeEmail: string;
  status: string;
  profiles: Profile[];
  expiresAt: string;
  createdAt: string;
  inviterName?: string;
  inviterEmail?: string;
}

interface ActiveShare {
  accessorUserId: string;
  accessorName: string;
  accessorEmail: string;
  profiles: Profile[];
  grantedAt: string;
}

interface SharedProfile {
  id: string;
  username: string;
  image: string;
  spotifyID: string;
  ownerUserId: string;
  ownerName: string;
  ownerEmail: string;
}

const TeamManagementTab = () => {
  const [user] = useState(() => JSON.parse(localStorage.getItem('userInfo') || '{}'));
  const userId = user?.userId;
  const { packageData, isLoading: packageLoading } = usePackageContext();

  // State for invite form
  const [inviteEmails, setInviteEmails] = useState<string[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  // State for user's profiles
  const [userProfiles, setUserProfiles] = useState<Profile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // State for sent invitations
  const [sentInvitations, setSentInvitations] = useState<Invitation[]>([]);
  const [loadingSentInvitations, setLoadingSentInvitations] = useState(false);

  // State for pending invitations (received)
  const [pendingInvitations, setPendingInvitations] = useState<Invitation[]>([]);
  const [loadingPendingInvitations, setLoadingPendingInvitations] = useState(false);

  // State for active shares (people with access to my profiles)
  const [activeShares, setActiveShares] = useState<ActiveShare[]>([]);
  const [loadingActiveShares, setLoadingActiveShares] = useState(false);

  // State for shared profiles (profiles shared with me)
  const [sharedProfiles, setSharedProfiles] = useState<SharedProfile[]>([]);
  const [loadingSharedProfiles, setLoadingSharedProfiles] = useState(false);

  // Check if user can share profiles (Pro or Unlimited)
  const canShareProfiles = useMemo(
    () =>
      getStripePlanType((packageData?.data?.[0] as any)?.planId) !== 'free' ||
      Boolean(getActiveWalletPlan()),
    [packageData]
  );

  // Fetch user's profiles
  useEffect(() => {
    if (userId) {
      fetchUserProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Fetch sent and pending invitations, active shares, and shared profiles
  useEffect(() => {
    if (userId && user?.email) {
      fetchSentInvitations();
      fetchPendingInvitations();
      fetchActiveShares();
      fetchSharedProfiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, user?.email]);

  const fetchUserProfiles = async () => {
    try {
      setLoadingProfiles(true);
      const response = await axios.get(`${BASE_URL}/user/${userId}/data`);
      if (response.data?.data) {
        setUserProfiles(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch profiles:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  const fetchSentInvitations = async () => {
    try {
      setLoadingSentInvitations(true);
      const response = await axios.get(`${BASE_URL}/invitations/sent`, {
        params: { userId },
      });
      if (response.data?.success) {
        setSentInvitations(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch sent invitations:', error);
    } finally {
      setLoadingSentInvitations(false);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      setLoadingPendingInvitations(true);
      const response = await axios.get(`${BASE_URL}/invitations/pending`, {
        params: { userId, email: user.email },
      });
      if (response.data?.success) {
        setPendingInvitations(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch pending invitations:', error);
    } finally {
      setLoadingPendingInvitations(false);
    }
  };

  const fetchActiveShares = async () => {
    try {
      setLoadingActiveShares(true);
      const response = await axios.get(`${BASE_URL}/shared-profiles/active-shares`, {
        params: { userId },
      });
      if (response.data?.success) {
        setActiveShares(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch active shares:', error);
    } finally {
      setLoadingActiveShares(false);
    }
  };

  const fetchSharedProfiles = async () => {
    try {
      setLoadingSharedProfiles(true);
      const response = await axios.get(`${BASE_URL}/shared-profiles`, {
        params: { userId },
      });
      if (response.data?.success) {
        setSharedProfiles(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch shared profiles:', error);
    } finally {
      setLoadingSharedProfiles(false);
    }
  };

  const handleSendInvitation = async () => {
    if (inviteEmails.length === 0) {
      toast.error('Please enter at least one email address');
      return;
    }

    if (selectedProfiles.length === 0) {
      toast.error('Please select at least one profile to share');
      return;
    }

    try {
      setSendingInvite(true);

      // Send invitation with all emails and profiles
      // Backend will handle per-profile validation
      const response = await axios.post(`${BASE_URL}/invitations/send`, {
        inviterUserId: userId,
        inviteeEmails: inviteEmails,
        profileIds: selectedProfiles,
        custId: user.custId,
        subId: user.subId,
      });

      if (response.data?.success) {
        const { successful, failed, skipped } = response.data.data;

        // Handle successful invitations
        if (successful && successful.length > 0) {
          successful.forEach((success: any) => {
            const profileCount = success.profileCount || selectedProfiles.length;
            const blockedCount = success.blockedCount || 0;

            if (blockedCount > 0 && success.blockedProfiles) {
              // Show which profiles were blocked
              success.blockedProfiles.forEach((blocked: any) => {
                toast.warning(`${success.email} - ${blocked.profileName}: ${blocked.reason}`);
              });
              toast.success(
                `Invitation sent to ${success.email} for ${profileCount} profile(s). ${blockedCount} profile(s) were skipped.`
              );
            } else {
              toast.success(`Invitation sent to ${success.email} for ${profileCount} profile(s)`);
            }
          });
        }

        // Handle skipped invitations
        if (skipped && skipped.length > 0) {
          skipped.forEach((skip: any) => {
            if (skip.blockedProfiles && skip.blockedProfiles.length > 0) {
              // Show detailed reasons for each blocked profile
              skip.blockedProfiles.forEach((blocked: any) => {
                toast.warning(`${skip.email} - ${blocked.profileName}: ${blocked.reason}`);
              });
            } else {
              toast.warning(`${skip.email}: ${skip.reason}`);
            }
          });
        }

        // Handle failed invitations
        if (failed && failed.length > 0) {
          failed.forEach((fail: any) => {
            toast.error(`${fail.email}: ${fail.reason}`);
          });
        }

        // Reset form if at least one invitation was successful
        if (successful && successful.length > 0) {
          setInviteEmails([]);
          setSelectedProfiles([]);
          fetchSentInvitations();
          fetchActiveShares();
        }
      }
    } catch (error: any) {
      console.error('Failed to send invitation:', error);

      // Handle specific error responses
      if (error.response?.data?.error === 'insufficient_plan') {
        toast.error('Only Pro and Unlimited users can share profiles');
      } else if (error.response?.status === 400 && error.response?.data) {
        // Handle validation errors with detailed messages
        const errorData = error.response.data;

        // Show detailed information about skipped/failed invitations
        if (errorData.data) {
          const { skipped, failed } = errorData.data;

          // Show skipped invitations with profile-level details
          if (skipped && skipped.length > 0) {
            skipped.forEach((skip: any) => {
              if (skip.blockedProfiles && skip.blockedProfiles.length > 0) {
                skip.blockedProfiles.forEach((blocked: any) => {
                  toast.warning(`${skip.email} - ${blocked.profileName}: ${blocked.reason}`, {
                    autoClose: 5000,
                  });
                });
              }
            });
          }

          // Show failed invitations
          if (failed && failed.length > 0) {
            failed.forEach((fail: any) => {
              toast.error(`${fail.email}: ${fail.reason}`);
            });
          }
        } else {
          // Show the main error message only if no detailed data is available
          toast.error(errorData.message || 'Failed to send invitation');
        }
      } else {
        toast.error(error.response?.data?.message || 'Failed to send invitation');
      }
    } finally {
      setSendingInvite(false);
    }
  };

  const handleCancelInvitation = (invitationId: string) => {
    Modal.confirm({
      title: 'Cancel invitation',
      content: 'Are you sure you want to cancel this invitation?',
      okText: 'Yes, cancel',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await axios.delete(`${BASE_URL}/invitations/${invitationId}`, {
            params: { userId },
          });
          toast.success('Invitation cancelled successfully');
          fetchSentInvitations();
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to cancel invitation');
        }
      },
    });
  };

  const handleAcceptInvitation = async (invitationId: string) => {
    try {
      const response = await axios.post(`${BASE_URL}/invitations/${invitationId}/accept`, {
        userId,
      });

      if (response.data?.success) {
        toast.success(
          'Invitation accepted! You now have access to the shared profiles. Open the navbar profile dropdown and switch to the shared profile to start managing it.'
        );
        fetchPendingInvitations();
        fetchActiveShares(); // Also refresh active shares
        fetchSharedProfiles(); // Refresh "Profiles Shared With Me" section

        // Dispatch event to notify SideBar to refetch shared profiles
        window.dispatchEvent(new Event('invitationAccepted'));
      }
    } catch (error: any) {
      console.error('❌ [TeamManagement] Failed to accept invitation:', error);
      toast.error(error.response?.data?.message || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = (invitationId: string) => {
    Modal.confirm({
      title: 'Decline Invitation',
      content: 'Are you sure you want to decline this invitation?',
      okText: 'Yes, decline',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await axios.post(`${BASE_URL}/invitations/${invitationId}/decline`, {
            userId,
          });
          toast.success('Invitation declined');
          fetchPendingInvitations();
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to decline invitation');
        }
      },
    });
  };

  const handleRevokeAccess = (accessorUserId: string, accessorName: string) => {
    Modal.confirm({
      title: 'Revoke access',
      content: `Are you sure you want to revoke ${accessorName}'s access to your profiles? They will no longer be able to manage your playlists.`,
      okText: 'Yes, revoke access',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await axios.delete(`${BASE_URL}/shared-profiles/${accessorUserId}/access`, {
            params: { userId },
          });
          toast.success('Access revoked successfully');
          fetchActiveShares();
          fetchSentInvitations(); // Refresh sent invitations to update status
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to revoke access');
        }
      },
    });
  };

  const handleLeaveSharedProfile = (profileId: string, profileName: string, ownerName: string) => {
    Modal.confirm({
      title: 'Leave Shared Profile',
      content: `Are you sure you want to leave ${ownerName}'s profile "${profileName}"? You will no longer have access to manage their playlists.`,
      okText: 'Yes, leave',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          const response = await axios.delete(`${BASE_URL}/shared-profiles/${profileId}/leave`, {
            params: { userId },
          });

          if (response.data?.success) {
            toast.success('You have successfully left this shared profile');

            // Refresh shared profiles list
            fetchSharedProfiles();

            // Dispatch event to notify MainHeader/SideBar to refetch shared profiles
            window.dispatchEvent(new Event('invitationAccepted'));

            // Check if user is currently on this shared profile
            const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser') || '{}');
            if (spotifyUser?.isShared === true && spotifyUser?.profileId === profileId) {
              // User is currently on the shared profile they just left
              // Switch back to their own profile
              toast.warning('Switching back to your profile...');

              // Fetch user's own profiles to switch back
              const userProfilesResponse = await axios.get(`${BASE_URL}/user/${userId}/data`);
              if (userProfilesResponse.data?.data && userProfilesResponse.data.data.length > 0) {
                const ownProfile = userProfilesResponse.data.data[0]; // Use first profile

                // Update localStorage with own tokens
                localStorage.setItem('SPOTIFY_TOKEN', ownProfile.token);
                localStorage.setItem('SPOTIFY_REFRESH_TOKEN', ownProfile.refresh);
                localStorage.setItem(
                  'spotifyUser',
                  JSON.stringify({
                    username: ownProfile.username,
                    profileId: ownProfile._id,
                    isShared: false,
                    code: ownProfile.token,
                    userId: userId,
                  })
                );

                // Reload page to apply changes
                setTimeout(() => {
                  window.location.replace('/home');
                }, 1000);
              } else {
                // No connected Spotify profile, clear shared tokens and prompt user to connect
                localStorage.removeItem('spotifyUser');
                localStorage.removeItem(SPOTIFY_TOKEN);
                localStorage.removeItem(SPOTIFY_REFRESH_TOKEN);
                localStorage.removeItem(SPOTIFY_CODE);
                localStorage.removeItem(SPOTIFY_USER_INFO);
                localStorage.setItem('hasSpotifyConnection', 'false');
                localStorage.removeItem('spotifyBannerDismissed');
                toast.warning('You do not have a connected Spotify profile. Please connect one to continue.');

                setTimeout(() => {
                  window.location.replace('/settings');
                }, 1000);
              }
            }
          }
        } catch (error: any) {
          console.error('Failed to leave shared profile:', error);
          toast.error(error.response?.data?.message || 'Failed to leave shared profile');
        }
      },
    });
  };

  const sentInvitationsColumns = [
    {
      title: 'Email',
      dataIndex: 'inviteeEmail',
      key: 'email',
    },
    {
      title: 'Profiles Shared',
      dataIndex: 'profiles',
      key: 'profiles',
      render: (profiles: Profile[]) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {profiles.map(profile => (
            <div key={profile._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar
                size="small"
                src={profile.image && profile.image !== 'dummy' ? profile.image : undefined}
                style={{
                  backgroundColor:
                    !profile.image || profile.image === 'dummy' ? '#2dce6e' : undefined,
                  color: '#ffffff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {(!profile.image || profile.image === 'dummy') &&
                  (profile.username?.length >= 2 ? profile.username[0] + profile.username[1] : 'U')}
              </Avatar>
              <span>{profile.username}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        let color = 'orange';
        if (status === 'accepted') color = 'green';
        if (status === 'declined' || status === 'expired') color = 'red';
        return <Tag color={color}>{status}</Tag>;
      },
    },
    {
      title: 'Sent',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => moment(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Invitation) => {
        if (record.status === 'pending') {
          return (
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleCancelInvitation(record.id)}
            >
              Cancel
            </Button>
          );
        }
        return null;
      },
    },
  ];

  const activeSharesColumns = [
    {
      title: 'User',
      key: 'user',
      render: (_: any, record: ActiveShare) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.accessorName}</div>
          <div style={{ fontSize: '12px', color: '#888' }}>{record.accessorEmail}</div>
        </div>
      ),
    },
    {
      title: 'Profiles',
      dataIndex: 'profiles',
      key: 'profiles',
      render: (profiles: Profile[]) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {profiles.map(profile => (
            <div key={profile._id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Avatar
                size="small"
                src={profile.image && profile.image !== 'dummy' ? profile.image : undefined}
                style={{
                  backgroundColor:
                    !profile.image || profile.image === 'dummy' ? '#2dce6e' : undefined,
                  color: '#ffffff',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                }}
              >
                {(!profile.image || profile.image === 'dummy') &&
                  (profile.username?.length >= 2 ? profile.username[0] + profile.username[1] : 'U')}
              </Avatar>
              <span>{profile.username}</span>
            </div>
          ))}
        </div>
      ),
    },
    {
      title: 'Since',
      dataIndex: 'grantedAt',
      key: 'grantedAt',
      render: (date: string) => moment(date).format('MMM D, YYYY'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: ActiveShare) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRevokeAccess(record.accessorUserId, record.accessorName)}
        >
          Revoke
        </Button>
      ),
    },
  ];

  return (
    <TeamManagementTabStyle>
      {/* Loading State */}
      {packageLoading && <LoadingSpinner text="Loading team management..." />}

      {/* Feature Gate for Free Users */}
      {!packageLoading && !canShareProfiles && (
        <div className="upgradePrompt">
          <CrownOutlined style={{ fontSize: 32, color: '#1890ff' }} />
          <h2>Upgrade to Pro or Unlimited</h2>
          <p style={{ color: 'white' }}>
            Share profiles with team members and collaborate on playlists
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <button
              className="addSong"
              onClick={() => window.open('/settings?tab=upgradeplan', '_blank')}
            >
              View Plans
            </button>
          </div>
        </div>
      )}

      {/* Invite Section - Only for Pro/Unlimited */}
      {canShareProfiles && (
        <div className="inviteSection">
          <h3>Invite team members</h3>
          <p className="sectionDescription">
            Share your Spotify profiles with team members to collaborate on playlists
          </p>

          <div className="inviteForm">
            <MultiEmailInput
              value={inviteEmails}
              onChange={setInviteEmails}
              placeholder="Enter email addresses (press Enter, comma, or space to add multiple)"
              disabled={loadingProfiles || sendingInvite}
            />

            <div className="profileSelection">
              <p className="selectionLabel">Select profiles to share:</p>
              {loadingProfiles ? (
                <LoadingOutlined style={{ fontSize: 24 }} spin />
              ) : (
                <div className="customCheckboxGroup">
                  {userProfiles.map(profile => (
                    <label
                      key={profile._id}
                      className={`customCheckbox ${
                        selectedProfiles.includes(profile._id) ? 'checked' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedProfiles.includes(profile._id)}
                        onChange={e => {
                          if (e.target.checked) {
                            setSelectedProfiles([...selectedProfiles, profile._id]);
                          } else {
                            setSelectedProfiles(selectedProfiles.filter(id => id !== profile._id));
                          }
                        }}
                      />
                      <span className="checkmark"></span>
                      <Avatar
                        size="small"
                        src={profile.image && profile.image !== 'dummy' ? profile.image : undefined}
                        style={{
                          marginRight: 8,
                          backgroundColor:
                            !profile.image || profile.image === 'dummy' ? '#2dce6e' : undefined,
                          color: '#ffffff',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                        }}
                      >
                        {(!profile.image || profile.image === 'dummy') &&
                          (profile.username?.length >= 2
                            ? profile.username[0] + profile.username[1]
                            : 'U')}
                      </Avatar>
                      <span className="profileName">{profile.username}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <Tooltip
              title={
                inviteEmails.length === 0 && selectedProfiles.length === 0
                  ? 'Add at least one email and select at least one profile to send an invitation'
                  : inviteEmails.length === 0
                  ? 'Add at least one email to send an invitation'
                  : selectedProfiles.length === 0
                  ? 'Select at least one profile to send an invitation'
                  : ''
              }
            >
              <Button
                type="primary"
                size="large"
                onClick={handleSendInvitation}
                loading={sendingInvite}
                disabled={inviteEmails.length === 0 || selectedProfiles.length === 0}
              >
                Send Invitation{inviteEmails.length > 1 ? 's' : ''}
              </Button>
            </Tooltip>
          </div>
        </div>
      )}

      {/* Pending Invitations Received */}
      {pendingInvitations.length > 0 && (
        <div className="pendingInvitationsSection">
          <h3>Pending Invitations</h3>
          <p className="sectionDescription">
            You have received invitations to collaborate on these profiles
          </p>

          {loadingPendingInvitations ? (
            <div className="loading">
              <LoadingOutlined style={{ fontSize: 28 }} spin />
            </div>
          ) : (
            <div className="invitationCards">
              {pendingInvitations.map(invitation => (
                <div key={invitation.id} className="invitationCard">
                  <div className="cardHeader">
                    <h4>{invitation.inviterName}</h4>
                    <span className="email">{invitation.inviterEmail}</span>
                  </div>

                  <div className="cardBody">
                    <p className="profileCount">
                      {invitation.profiles.length} profile
                      {invitation.profiles.length > 1 ? 's' : ''} shared
                    </p>

                    <div className="profiles">
                      {invitation.profiles.map(profile => (
                        <div key={profile._id} className="profileItem">
                          <Avatar
                            size="small"
                            src={
                              profile.image && profile.image !== 'dummy' ? profile.image : undefined
                            }
                            style={{
                              backgroundColor:
                                !profile.image || profile.image === 'dummy' ? '#2dce6e' : undefined,
                              color: '#ffffff',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                            }}
                          >
                            {(!profile.image || profile.image === 'dummy') &&
                              (profile.username?.length >= 2
                                ? profile.username[0] + profile.username[1]
                                : 'U')}
                          </Avatar>
                          <span>{profile.username}</span>
                        </div>
                      ))}
                    </div>

                    <p className="expiryNote">Expires {moment(invitation.expiresAt).fromNow()}</p>
                  </div>

                  <div className="cardActions">
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      onClick={() => handleAcceptInvitation(invitation.id)}
                    >
                      Accept
                    </Button>
                    <Button danger onClick={() => handleDeclineInvitation(invitation.id)}>
                      Decline
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Active Shares (Team members with access) */}
      {canShareProfiles && activeShares.length > 0 && (
        <div className="activeSharesSection">
          <h3>Team members with access</h3>
          <p className="sectionDescription">Users who currently have access to your profiles</p>

          {loadingActiveShares ? (
            <div className="loading">
              <LoadingOutlined style={{ fontSize: 28 }} spin />
            </div>
          ) : (
            <Table
              dataSource={activeShares}
              columns={activeSharesColumns}
              rowKey="accessorUserId"
              pagination={{ pageSize: 10 }}
            />
          )}
        </div>
      )}

      {/* Shared With Me Section */}
      {sharedProfiles.length > 0 && (
        <div className="sharedWithMeSection">
          <h3>Profiles Shared With Me</h3>
          <p className="sectionDescription">Spotify profiles that others have shared with you</p>

          {loadingSharedProfiles ? (
            <div className="loading">
              <LoadingOutlined style={{ fontSize: 28 }} spin />
            </div>
          ) : (
            <div className="profileCards">
              {sharedProfiles.map(profile => (
                <div key={profile.id} className="profileCard">
                  <Avatar
                    size={64}
                    src={profile.image && profile.image !== 'dummy' ? profile.image : undefined}
                    style={{
                      backgroundColor:
                        !profile.image || profile.image === 'dummy' ? '#2dce6e' : undefined,
                      color: '#ffffff',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      fontSize: '24px',
                    }}
                  >
                    {(!profile.image || profile.image === 'dummy') &&
                      (profile.username?.length >= 2
                        ? profile.username[0] + profile.username[1]
                        : 'U')}
                  </Avatar>
                  <div className="profileName">{profile.username}</div>
                  <div className="ownerName">Owned by {profile.ownerName}</div>
                  <Button
                    danger
                    icon={<LogoutOutlined />}
                    onClick={() =>
                      handleLeaveSharedProfile(profile.id, profile.username, profile.ownerName)
                    }
                  >
                    Leave Profile
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sent invitations */}
      {canShareProfiles && (
        <div className="sentInvitationsSection">
          <h3>Sent invitations</h3>
          <p className="sectionDescription">Track invitations you've sent to team members</p>

          {loadingSentInvitations ? (
            <div className="loading">
              <LoadingOutlined style={{ fontSize: 28 }} spin />
            </div>
          ) : (
            <Table
              dataSource={sentInvitations}
              columns={sentInvitationsColumns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
            />
          )}
        </div>
      )}
    </TeamManagementTabStyle>
  );
};

export default TeamManagementTab;
